/**
 * Shared, extensible map symbology preset contract (`PRM-151`).
 *
 * This module defines a typed, presentation-only contract that lets
 * `apps/prism2` Map Tools register categorical (and, in the future,
 * graduated) visualization presets for a map layer family without a new
 * top-level UI pattern per preset. Consumers (e.g. a future Style/Symbology
 * tab) read preset metadata to render controls; they must not invent their
 * own ad-hoc filter or renderer logic per preset.
 *
 * Presets here are pure data — no `@arcgis/core` renderer instances are
 * constructed until a `rendererFactory` is invoked by a consumer, and no
 * ArcGIS layer instances or view state are stored on the preset itself
 * (per spec section 7.4.4: preset state must remain view-local, never in Redux).
 *
 * The acquisition phase/status preset values below are confirmed against
 * `packages/map/docs/map-service-defs/aim/opportunityeditmap/potential-acquisitions.json`
 * (`PotentialStatus` domain: `Pipeline`, `Negotiation`, `Under Contract`,
 * `Completed`, `Discontinued`). `Discontinued` currently combines several
 * terminal Prism outcomes (canceled, failed, rejected, declined, historic) —
 * this module must never claim those are individually distinguishable from
 * GIS data alone (spec section 9).
 */

/**
 * The map layer "roles" a preset's category filters/renderers may target.
 * A preset may supply renderer/filter metadata for one or both roles so
 * boundary and pin layers can share semantic categories while using
 * different ArcGIS symbols (spec section 7.2.3).
 */
export type SymbologyLayerRole = 'boundary' | 'pin';

/**
 * Discriminated preset kind. The MVP only registers `categorical` presets;
 * `graduated` is included now so the contract does not need to be redesigned
 * when acreage/price ramps are approved (spec sections 7.2.2 and 7.7).
 */
export type SymbologyPresetKind = 'categorical' | 'graduated';

/**
 * One selectable category within a categorical preset (e.g. one
 * `PotentialStatus` value). `value` must be the raw GIS-coded value used in
 * renderer `uniqueValueInfos`/WHERE clauses — never a display label (spec
 * section 9). `where` is an optional standalone WHERE fragment (no leading
 * `AND`/parentheses required by the caller) that isolates this category;
 * required for a category whose `value` cannot be matched with a simple
 * `field = 'value'` equality (e.g. an explicit multi-condition Unknown
 * bucket).
 */
export interface SymbologyCategory<TValue extends string = string> {
  /** Stable, GIS-coded renderer/filter value (never a display label). */
  value: TValue;
  /** Human-readable label for legend/toggle UI. */
  label: string;
  /** Fill/outline or pin color for this category. */
  color: string;
  /** Optional additional explanatory text (e.g. Discontinued's combined-outcomes caveat). */
  description?: string;
  /**
   * Standalone WHERE fragment isolating this category on the target field.
   * Falls back to `` `${field} = 'value'` `` when omitted, so most
   * categories don't need to restate the trivial case.
   */
  where?: string;
  /** Whether this category is excluded from the default (initial) view. */
  excludedByDefault?: boolean;
}

/** Categorical preset backed by a single named field and coded category values. */
export interface CategoricalSymbologyPreset<TValue extends string = string> {
  kind: 'categorical';
  /** Stable preset identifier — never a display label; safe for view-local state keys. */
  id: string;
  /** Human-readable preset name for Style tab UI. */
  label: string;
  /** GIS field the categories are keyed on (renderer field / WHERE target). */
  field: string;
  /** Layer roles this preset supplies renderer/filter metadata for. */
  targetRoles: SymbologyLayerRole[];
  /** Ordered categories, including an explicit Unknown/other bucket where applicable. */
  categories: SymbologyCategory<TValue>[];
}

/** Class break for a graduated (numeric ramp) preset. Deferred from the MVP UI, but representable now. */
export interface GraduatedSymbologyClassBreak {
  minValue: number;
  maxValue: number;
  label: string;
  color: string;
}

/** Graduated preset backed by a numeric field and class-break ramp. Not surfaced by the MVP Style tab. */
export interface GraduatedSymbologyPreset {
  kind: 'graduated';
  id: string;
  label: string;
  /** Numeric GIS field the class breaks are computed against. */
  field: string;
  targetRoles: SymbologyLayerRole[];
  classBreaks: GraduatedSymbologyClassBreak[];
  /** Sequential color ramp identifier/name for documentation and future renderer wiring. */
  rampName: string;
}

/** Any registered symbology preset. */
export type SymbologyPreset = CategoricalSymbologyPreset | GraduatedSymbologyPreset;

/**
 * Type guard narrowing a {@link SymbologyPreset} to its categorical variant.
 *
 * @param preset - The preset to check.
 * @returns `true` if `preset.kind === 'categorical'`.
 *
 * @example
 * isCategoricalPreset(acquisitionStatusPreset) // true
 */
export function isCategoricalPreset(preset: SymbologyPreset): preset is CategoricalSymbologyPreset {
  return preset.kind === 'categorical';
}

/**
 * Type guard narrowing a {@link SymbologyPreset} to its graduated variant.
 *
 * @param preset - The preset to check.
 * @returns `true` if `preset.kind === 'graduated'`.
 */
export function isGraduatedPreset(preset: SymbologyPreset): preset is GraduatedSymbologyPreset {
  return preset.kind === 'graduated';
}

// ── Acquisition phase/status preset (PRM-151 MVP) ───────────────────────────

/** The GIS field the acquisition phase/status preset is keyed on. */
export const ACQUISITION_STATUS_FIELD = 'PotentialStatus' as const;

/**
 * The exact confirmed `PotentialStatus` coded values (excluding the explicit
 * `Unknown` bucket), per
 * `packages/map/docs/map-service-defs/aim/opportunityeditmap/potential-acquisitions.json`.
 */
export const ACQUISITION_STATUS_CODES = {
  Pipeline: 'Pipeline',
  Negotiation: 'Negotiation',
  UnderContract: 'Under Contract',
  Completed: 'Completed',
  Discontinued: 'Discontinued'
} as const;

/** A confirmed `PotentialStatus` GIS coded value. */
export type AcquisitionStatusCode = (typeof ACQUISITION_STATUS_CODES)[keyof typeof ACQUISITION_STATUS_CODES];

/** The explicit Unknown bucket key for null/blank/unexpected `PotentialStatus` values. */
export const ACQUISITION_STATUS_UNKNOWN = 'Unknown' as const;

/** Every value the acquisition phase/status preset can classify a feature into. */
export type AcquisitionStatusValue = AcquisitionStatusCode | typeof ACQUISITION_STATUS_UNKNOWN;

/**
 * WHERE fragment (no leading `AND`) isolating the explicit Unknown bucket:
 * null, blank, or any value outside the five confirmed `PotentialStatus`
 * codes. Mirrors the pattern used by `mgmtTypeUnknownWhere` in
 * `./mgmtType.ts` for consistency across preset contracts.
 */
export const acquisitionStatusUnknownWhere =
  `(${ACQUISITION_STATUS_FIELD} IS NULL OR ${ACQUISITION_STATUS_FIELD} = '' OR ${ACQUISITION_STATUS_FIELD} NOT IN ` +
  `('Pipeline','Negotiation','Under Contract','Completed','Discontinued'))`;

/**
 * The categorical acquisition phase/status preset. Unlike the Default
 * rendering, this preset starts with every supported category visible so the
 * legend can independently toggle Completed and Discontinued on or off. The
 * Default rendering preserves the existing active-only behavior through
 * `acquisitionActiveOnlyWhere` in `./renderers.ts`.
 *
 * `Discontinued` is documented here as combining several terminal Prism
 * outcomes (canceled, failed, rejected, declined, historic) per the current
 * GIS synchronization contract; the UI must surface this caveat rather than
 * implying finer-grained distinction is available (spec sections 7.3.6 and 9).
 */
export const acquisitionStatusPreset: CategoricalSymbologyPreset<AcquisitionStatusValue> = {
  kind: 'categorical',
  id: 'acquisition-status',
  label: 'Acquisition Phase / Status',
  field: ACQUISITION_STATUS_FIELD,
  targetRoles: ['boundary', 'pin'],
  // Colors below form a Pipeline -> Completed lifecycle progression (light
  // green -> yellow -> orange/red -> dark green) so the phase sequence reads
  // at a glance. Purple/violet is intentionally avoided anywhere in this
  // preset — `propertyColors.lease` (`rgba(204,115,255,.75)`, see
  // `./constants.ts`) already owns that hue for Lease property symbology, and
  // reusing it here would make acquisition phase pins/boundaries visually
  // ambiguous with leased properties on the same map. Discontinued and
  // Unknown remain distinct, desaturated colors outside the progression so
  // terminal/unclassified states never get confused with an active phase.
  categories: [
    {
      // Distinct from the MgmtType `DirectOp` blue (`propertyColors.directOperations`,
      // `#5e83ff`) per the shared MgmtType color contract in `./mgmtType.ts` — Pipeline
      // previously used a very similar blue (`rgba(96, 165, 250, 1)`), which could be
      // visually confused with a DirectOp-managed property boundary/pin.
      value: ACQUISITION_STATUS_CODES.Pipeline,
      label: 'Pipeline',
      color: 'rgba(74, 222, 128, 1)'
    },
    {
      value: ACQUISITION_STATUS_CODES.Negotiation,
      label: 'Negotiation',
      color: 'rgba(250, 204, 21, 1)'
    },
    {
      value: ACQUISITION_STATUS_CODES.UnderContract,
      label: 'Under Contract',
      color: 'rgba(249, 115, 22, 1)'
    },
    {
      value: ACQUISITION_STATUS_CODES.Completed,
      label: 'Completed',
      color: 'rgba(21, 128, 61, 1)'
    },
    {
      value: ACQUISITION_STATUS_CODES.Discontinued,
      label: 'Discontinued',
      color: 'maroon',
      description:
        'Combines several terminal outcomes (canceled, failed, rejected, declined, historic) that are not individually distinguishable from current GIS data.'
    },
    {
      value: ACQUISITION_STATUS_UNKNOWN,
      label: 'Unknown',
      color: 'rgba(148, 163, 184, 1)',
      description: 'Null, blank, or unexpected PotentialStatus values.',
      where: acquisitionStatusUnknownWhere
    }
  ]
};

/**
 * Resolves the effective standalone WHERE fragment for one category of a
 * categorical preset, falling back to a simple field equality when the
 * category does not supply its own `where` override.
 *
 * @param preset - The categorical preset the category belongs to.
 * @param category - The category to build a WHERE fragment for.
 * @returns A standalone WHERE fragment (no leading `AND`, no surrounding parens required by the caller).
 *
 * @example
 * categoryWhereClause(acquisitionStatusPreset, acquisitionStatusPreset.categories[0])
 * // "PotentialStatus = 'Pipeline'"
 * categoryWhereClause(acquisitionStatusPreset, acquisitionStatusPreset.categories.at(-1)!)
 * // "(PotentialStatus IS NULL OR PotentialStatus = '' OR PotentialStatus NOT IN (...))"
 */
export function categoryWhereClause<TValue extends string>(
  preset: CategoricalSymbologyPreset<TValue>,
  category: SymbologyCategory<TValue>
): string {
  if (category.where) return category.where;
  return `${preset.field} = '${category.value}'`;
}
